import { useEffect } from 'react';
import { EnterpriseStructure, OwnershipType, OwnershipDetailsType } from '@brighte/validation';
import { FormValues } from '@graphql/types';
import { useApplicationData, useSetApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { Field } from '@spark-web/field';
import { Select } from '@spark-web/select';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';

export type SignatoryInfo = {
  name: string;
  email: string;
};

export type SignatoriesData = {
  primarySignatory: SignatoryInfo;
  secondarySignatory?: SignatoryInfo;
};

type Director = {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

function getFullName(director: Director): string {
  return [director.firstName, director.middleName, director.lastName].filter(Boolean).join(' ');
}

function getDirectors(ownershipDetails: OwnershipDetailsType | undefined): Director[] {
  if (!ownershipDetails?.enterprise) return [];

  const enterprise = ownershipDetails.enterprise;

  switch (enterprise.structure) {
    case EnterpriseStructure.Trust: {
      const trust = enterprise.trust;
      if (trust && 'directors' in trust) {
        return (trust.directors as Director[]) ?? [];
      }
      return [];
    }
    case EnterpriseStructure.Partnership: {
      const partnership = enterprise.partnership;
      if (partnership && 'directors' in partnership) {
        return (partnership.directors as Director[]) ?? [];
      }
      return [];
    }
    case EnterpriseStructure.ProprietaryLimitedCompany: {
      const ptyLtd = enterprise.proprietaryLimitedCompany;
      if (ptyLtd && 'directors' in ptyLtd) {
        return (ptyLtd.directors as Director[]) ?? [];
      }
      return [];
    }
    case EnterpriseStructure.Company: {
      const company = enterprise.company;
      if (company && 'directors' in company) {
        return (company.directors as Director[]) ?? [];
      }
      return [];
    }
    default:
      return [];
  }
}

function getOwnershipType(ownershipDetails: OwnershipDetailsType | undefined): OwnershipType | undefined {
  if (!ownershipDetails?.enterprise) return undefined;

  const enterprise = ownershipDetails.enterprise;

  switch (enterprise.structure) {
    case EnterpriseStructure.Trust: {
      if ('trust' in enterprise && enterprise.trust && 'ownershipType' in enterprise.trust) {
        return enterprise.trust.ownershipType as OwnershipType;
      }
      return undefined;
    }
    case EnterpriseStructure.Partnership: {
      if ('partnership' in enterprise && enterprise.partnership && 'ownershipType' in enterprise.partnership) {
        return enterprise.partnership.ownershipType as OwnershipType;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

function getSecondarySignatoryLabel(ownershipDetails: OwnershipDetailsType | undefined): string {
  if (!ownershipDetails?.enterprise) return 'Select another signatory';

  const structure = ownershipDetails.enterprise.structure;
  const ownershipType = getOwnershipType(ownershipDetails);

  if (structure === EnterpriseStructure.ProprietaryLimitedCompany) {
    return 'Select a director from the corporate entity that is different from the one Signatory 1 belongs to';
  }
  if (
    (structure === EnterpriseStructure.Trust || structure === EnterpriseStructure.Partnership) &&
    ownershipType === OwnershipType.Corporate
  ) {
    return 'Select a director from the corporate entity that is different from the one Signatory 1 belongs to';
  }
  return 'Select another signatory';
}

export function hasSecondarySignatory(ownershipDetails: OwnershipDetailsType | undefined): boolean {
  if (!ownershipDetails?.enterprise) return false;

  const structure = ownershipDetails.enterprise.structure;
  const directors = getDirectors(ownershipDetails);
  const ownershipType = getOwnershipType(ownershipDetails);

  switch (structure) {
    case EnterpriseStructure.SoleTrader:
    case EnterpriseStructure.Other:
      return false;

    case EnterpriseStructure.Trust:
      if (ownershipType === OwnershipType.Individual) {
        return directors.length >= 2;
      }
      if (ownershipType === OwnershipType.Corporate) {
        return true;
      }
      return false;

    case EnterpriseStructure.Partnership:
      if (ownershipType === OwnershipType.Individual) {
        return directors.length >= 2;
      }
      if (ownershipType === OwnershipType.Corporate) {
        return true;
      }
      return false;

    case EnterpriseStructure.ProprietaryLimitedCompany:
      return directors.length >= 2;

    case EnterpriseStructure.Company:
      return directors.length >= 2;

    case EnterpriseStructure.IncorporatedAssociation:
    case EnterpriseStructure.UnincorporatedAssociation:
      return false;

    default:
      return false;
  }
}

type SecondarySelectProps = {
  options: Array<{ label: string; value: string }>;
  onChange: (value: string) => void;
};

function SecondarySelect({ options, onChange }: SecondarySelectProps) {
  return (
    <Select
      options={options}
      placeholder="Select"
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
    />
  );
}

export function SignatorySection() {
  const businessDetails = useApplicationData('BusinessDetails');
  const contactDetails = useApplicationData('ContactDetails');
  const ownershipDetails = useApplicationData('OwnershipDetails');
  const setApplicationData = useSetApplicationData();

  const primaryFirstName = businessDetails?.applicant?.firstName ?? '';
  const primaryLastName = businessDetails?.applicant?.lastName ?? '';
  const primaryName = [primaryFirstName, primaryLastName].filter(Boolean).join(' ') || 'Primary applicant';
  const primaryEmail = contactDetails?.email ?? '';

  const showSecondary = hasSecondarySignatory(ownershipDetails as OwnershipDetailsType | undefined);
  const dropdownLabel = getSecondarySignatoryLabel(ownershipDetails as OwnershipDetailsType | undefined);

  const directors = getDirectors(ownershipDetails as OwnershipDetailsType | undefined);

  const secondaryOptions = directors
    .slice(1)
    .map((director, slicedIndex) => ({ director, originalIndex: slicedIndex + 1 }))
    .filter(({ director }) => director.firstName || director.lastName)
    .map(({ director, originalIndex }) => {
      const name = getFullName(director);
      const email = director.email ?? '';
      const label = email ? `${name} (${email})` : name;
      return { label, value: String(originalIndex) };
    });

  useEffect(() => {
    setApplicationData({
      Signatories: {
        primarySignatory: {
          name: primaryName,
          email: primaryEmail,
        },
      },
    } as unknown as Partial<FormValues>);
  }, [primaryName, primaryEmail, setApplicationData]);

  const handleSecondaryChange = (value: string) => {
    if (!value) {
      setApplicationData({
        Signatories: {
          primarySignatory: { name: primaryName, email: primaryEmail },
        },
      } as unknown as Partial<FormValues>);
      return;
    }

    const directorIndex = parseInt(value, 10);
    const director = directors[directorIndex];
    if (!director) return;

    const name = getFullName(director);
    const email = director.email ?? '';

    setApplicationData({
      Signatories: {
        primarySignatory: { name: primaryName, email: primaryEmail },
        secondarySignatory: { name, email },
      },
    } as unknown as Partial<FormValues>);
  };

  return (
    <Stack gap="xlarge">
      <Heading level="3">Signatories</Heading>

      <Stack gap="medium">
        <Text weight="semibold">Signatory 1</Text>
        <Box border="neutral" borderRadius="small" padding="large">
          <Stack gap="medium">
            <Text weight="semibold">{primaryName}</Text>
            {primaryEmail ? <Text tone="muted">{primaryEmail}</Text> : null}
          </Stack>
        </Box>
      </Stack>

      {showSecondary && secondaryOptions.length > 0 && (
        <Stack gap="medium">
          <Text weight="semibold">Signatory 2</Text>
          <Field label={dropdownLabel}>
            <SecondarySelect options={secondaryOptions} onChange={handleSecondaryChange} />
          </Field>
        </Stack>
      )}
      {showSecondary && directors.length <= 1 && (
        <Stack gap="medium">
          <Text weight="semibold">Signatory 2</Text>
          <Text tone="critical">
            A second signatory is required but no additional directors are available. Please add another director in the
            Ownership Details step.
          </Text>
        </Stack>
      )}
      <Divider />
    </Stack>
  );
}

export default SignatorySection;
